import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';

interface SidebarContextType {
  isOpen: boolean;
  collapsed: boolean;
  width: number;
  isMobile: boolean;
  toggleSidebar: () => void;
  toggleCollapse: () => void;
  closeSidebar: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

const DRAWER_WIDTH = 280;
const COLLAPSED_WIDTH = 64;

interface SidebarProviderProps {
  children: ReactNode;
}

export const SidebarProvider: React.FC<SidebarProviderProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // Load desktop preferences on mount
  useEffect(() => {
    if (!isMobile) {
      const savedOpen = localStorage.getItem('sidebarOpen');
      const savedCollapsed = localStorage.getItem('sidebarCollapsed');

      if (savedOpen !== null) {
        setDesktopOpen(JSON.parse(savedOpen));
      }
      if (savedCollapsed !== null) {
        setCollapsed(JSON.parse(savedCollapsed));
      }
    }
  }, [isMobile]);

  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setMobileOpen(prev => !prev);
    } else {
      setDesktopOpen(prev => {
        const newOpen = !prev;
        localStorage.setItem('sidebarOpen', JSON.stringify(newOpen));
        return newOpen;
      });
    }
  }, [isMobile]);

  const toggleCollapse = useCallback(() => {
    if (!isMobile) {
      setCollapsed(prev => {
        const newCollapsed = !prev;
        localStorage.setItem('sidebarCollapsed', JSON.stringify(newCollapsed));
        return newCollapsed;
      });
    }
  }, [isMobile]);

  const closeSidebar = useCallback(() => {
    if (isMobile) {
      setMobileOpen(false);
    }
  }, [isMobile]);

  // Keyboard shortcut for toggling sidebar
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'b') {
        e.preventDefault();
        toggleSidebar();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [toggleSidebar]);

  const isOpen = isMobile ? mobileOpen : desktopOpen;
  const width = collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  // Memoized so consumers (MainLayout, topbar) only re-render when sidebar
  // state actually changes, not on every provider render.
  const value: SidebarContextType = useMemo(
    () => ({
      isOpen,
      collapsed,
      width,
      isMobile,
      toggleSidebar,
      toggleCollapse,
      closeSidebar,
    }),
    [isOpen, collapsed, width, isMobile, toggleSidebar, toggleCollapse, closeSidebar],
  );

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = (): SidebarContextType => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};
